import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
    ArrowLeft, Mail, MessageSquare, MessagesSquare, Megaphone,
    ShieldCheck, Info, Save, AlertTriangle, CheckCircle2, X,
} from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton } from "@/components/BookingShell";

/**
 * Sprint 34 — Global communication + marketing defaults.
 *
 * Split cleanly into two sections:
 *  - Service comms defaults (booking confirmation / reminders / aftercare / payment)
 *  - Marketing defaults (opt-in-only channels; PECR-compliant)
 *
 * Bulk actions live at the bottom, every destructive/broad action is behind
 * a per-action confirm dialog so nothing sends by accident.
 */
export function CommunicationDefaults() {
    const [defaults, setDefaults] = useState(null);
    const [saving, setSaving] = useState(false);
    const [confirm, setConfirm] = useState(null); // { kind, title, message, action }
    const [result, setResult] = useState(null); // { kind, title, payload }

    const load = () => staffApi.getCommunicationDefaults().then((r) => setDefaults(r.defaults)).catch(() => setDefaults({}));
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!defaults || saving) return;
        setSaving(true);
        try {
            const r = await staffApi.setCommunicationDefaults(defaults);
            setDefaults(r.defaults);
            toast.success("Defaults saved");
        } catch (e) { toast.error(e?.message || "Save failed"); }
        finally { setSaving(false); }
    };

    const BULK_LABELS = {
        "service": {
            title: "Service defaults applied",
            summary: "Essential email / SMS / WhatsApp preferences pushed to the whole directory.",
        },
        "soft-opt-in": {
            title: "Email marketing enabled",
            summary: "Legally-eligible customers (paid appointment in the last 24 months, no opt-out) are now on email marketing.",
        },
        "disable-all": {
            title: "Marketing disabled everywhere",
            summary: "Every marketing channel is off and an opt-out timestamp has been recorded for the directory.",
        },
    };

    const runBulk = (kind) => async () => {
        setSaving(true);
        try {
            let r;
            if (kind === "service") r = await staffApi.applyServiceCommsDefaults();
            else if (kind === "soft-opt-in") r = await staffApi.applyMarketingSoftOptIn();
            else if (kind === "disable-all") r = await staffApi.disableAllMarketing();
            setConfirm(null);
            const info = BULK_LABELS[kind] || { title: "Bulk action complete", summary: "" };
            setResult({ kind, title: info.title, summary: info.summary, payload: r || {} });
            toast.success(`${info.title} — ${r?.updated ?? r?.enabled ?? r?.modified ?? 0} record${(r?.updated ?? r?.enabled ?? r?.modified ?? 0) === 1 ? "" : "s"} updated`);
        } catch (e) { toast.error(e?.message || "Bulk action failed"); }
        finally { setSaving(false); }
    };

    if (!defaults) return <Skeleton className="h-64" />;

    const set = (k, v) => setDefaults({ ...defaults, [k]: v });

    return (
        <div className="space-y-6" data-testid="comms-defaults-page">
            <div>
                <Link to="/staff" className="text-[10px] uppercase tracking-[0.18em] text-white/50 hover:text-gold inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Back
                </Link>
                <h1 className="mt-2 font-serif font-light text-3xl md:text-4xl text-white">
                    Communications & marketing
                </h1>
                <p className="mt-2 text-sm text-white/60 max-w-2xl">
                    Set the default channels used for service and marketing comms. Service comms (bookings, reminders, payment links, aftercare) are always considered essential and are never gated on marketing consent.
                </p>
            </div>

            {/* Service comms */}
            <Section
                icon={<MessagesSquare className="h-4 w-4" />}
                title="Service communications"
                blurb="Sent automatically for bookings, reminders, cancellations, payment links, medical forms and aftercare. Always classified as essential."
                testId="section-service"
            >
                <ChannelRow icon={<Mail className="h-4 w-4" />} label="Email" value={defaults.service_email_default} onChange={(v) => set("service_email_default", v)} testKey="service-email" />
                <ChannelRow icon={<MessageSquare className="h-4 w-4" />} label="SMS" value={defaults.service_sms_default} onChange={(v) => set("service_sms_default", v)} testKey="service-sms" />
                <ChannelRow icon={<MessageSquare className="h-4 w-4" />} label="WhatsApp" value={defaults.service_whatsapp_default} onChange={(v) => set("service_whatsapp_default", v)} testKey="service-whatsapp" />
            </Section>

            {/* Marketing */}
            <Section
                icon={<Megaphone className="h-4 w-4" />}
                title="Marketing communications"
                blurb={
                    <>
                        Promotions, birthday offers, win-back campaigns. Requires opt-in per ICO PECR. Never enabled automatically for existing customers unless they qualify for the strict soft opt-in below.
                    </>
                }
                testId="section-marketing"
            >
                <ChannelRow icon={<Mail className="h-4 w-4" />} label="Email" value={defaults.marketing_email_default} onChange={(v) => set("marketing_email_default", v)} testKey="marketing-email" />
                <ChannelRow icon={<MessageSquare className="h-4 w-4" />} label="SMS" value={defaults.marketing_sms_default} onChange={(v) => set("marketing_sms_default", v)} testKey="marketing-sms" />
                <ChannelRow icon={<MessageSquare className="h-4 w-4" />} label="WhatsApp" value={defaults.marketing_whatsapp_default} onChange={(v) => set("marketing_whatsapp_default", v)} testKey="marketing-whatsapp" />
                <div className="rounded-xl bg-amber-500/10 border border-amber-400/30 p-3 text-xs text-amber-100 flex gap-2 mt-2">
                    <Info className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>
                        These defaults only apply to <strong>future customers</strong> at booking-time. To enable marketing for existing customers, use the ICO soft-opt-in bulk action below.
                    </span>
                </div>
            </Section>

            <div className="flex gap-2 justify-end">
                <button onClick={save} disabled={saving} data-testid="comms-defaults-save"
                        className="h-11 px-6 rounded-full bg-gold text-navy font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-40">
                    <Save className="h-4 w-4" /> Save defaults
                </button>
            </div>

            {/* Bulk actions */}
            <Section
                icon={<ShieldCheck className="h-4 w-4" />}
                title="Bulk actions on existing customers"
                blurb="These update every existing customer at once. Individual preferences remain editable afterwards, and recorded opt-outs are never overridden."
                testId="section-bulk"
            >
                <BulkRow
                    label="Apply service defaults to all customers"
                    hint="Overwrites the essential email/SMS/WhatsApp channels for every existing customer with the values above."
                    testId="bulk-apply-service"
                    onClick={() => setConfirm({
                        kind: "service",
                        title: "Apply service defaults to all customers?",
                        message: "This will overwrite the essential email/SMS/WhatsApp preferences on every existing customer with the settings above. Marketing preferences are not affected.",
                        run: runBulk("service"),
                    })}
                />
                <BulkRow
                    label="Enable email marketing for legally-eligible customers"
                    hint="Turns on email marketing (SMS/WhatsApp not enabled) for anyone who booked a paid appointment in the last 24 months and has never opted out. Records the ICO PECR soft opt-in as the consent basis."
                    testId="bulk-apply-soft-opt-in"
                    onClick={() => setConfirm({
                        kind: "soft-opt-in",
                        title: "Enable email marketing for eligible customers?",
                        message: "Anyone who has booked a paid appointment in the last 24 months and has never opted out will receive email marketing. Recorded consent basis: ICO PECR soft opt-in. SMS and WhatsApp are NOT enabled — those channels require explicit consent.",
                        run: runBulk("soft-opt-in"),
                    })}
                />
                <BulkRow
                    label="Disable all marketing for every customer"
                    hint="Turns off marketing on every channel for every customer and stamps an opt-out record. Use this if you're pausing all marketing."
                    danger
                    testId="bulk-disable-all"
                    onClick={() => setConfirm({
                        kind: "disable-all",
                        title: "Disable all marketing?",
                        message: "This turns off email, SMS and WhatsApp marketing for every customer and records an opt-out timestamp. To restart marketing later, customers will need to re-opt-in individually or via the soft opt-in bulk action.",
                        run: runBulk("disable-all"),
                    })}
                />
            </Section>

            {confirm && (
                <ConfirmModal
                    title={confirm.title}
                    message={confirm.message}
                    saving={saving}
                    onCancel={() => setConfirm(null)}
                    onConfirm={confirm.run}
                    danger={confirm.kind === "disable-all"}
                />
            )}

            {result && (
                <BulkResultModal
                    result={result}
                    onClose={() => setResult(null)}
                />
            )}

            <TestCommsPanel />
        </div>
    );
}

function TestCommsPanel() {
    const [templates, setTemplates] = useState([]);
    const [channel, setChannel] = useState("email");
    const [templateId, setTemplateId] = useState("");
    const [to, setTo] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("Hi, this is a test message from the Pout portal.");
    const [status, setStatus] = useState(null); // { status, to, channel, error?, message? }
    const [sending, setSending] = useState(false);
    const [liveDelivery, setLiveDelivery] = useState(null); // sent | delivered | read | failed

    useEffect(() => {
        staffApi.listMarketingTemplates()
            .then((r) => setTemplates(r.templates || []))
            .catch(() => {});
    }, []);

    // Sprint 36.7 — filter the dropdown by channel so WhatsApp only shows
    // templates that are actually linked to an APPROVED Meta template.
    // Local-only sample templates (e.g. Birthday Offer, Black Friday,
    // Christmas Offers, Monthly Newsletter, New Treatment Launch) are
    // hidden from the WhatsApp selector until they are Meta-backed.
    const visibleTemplates = useMemo(() => {
        if (channel === "whatsapp") {
            return templates.filter((t) => t.whatsapp_available);
        }
        return templates;
    }, [templates, channel]);
    // Clear the selected template if it isn't available on the newly-chosen
    // channel, so we never send with a stale non-WhatsApp id.
    useEffect(() => {
        if (templateId && !visibleTemplates.find((t) => t.id === templateId)) {
            setTemplateId("");
        }
    }, [visibleTemplates, templateId]);

    // Poll the wamid for delivery/read receipts after a successful acceptance.
    useEffect(() => {
        if (!status?.provider_message_id) return undefined;
        let cancelled = false;
        const wamid = status.provider_message_id;
        const start = Date.now();
        const poll = async () => {
            if (cancelled) return;
            try {
                const r = await staffApi.whatsappWamidLookup(wamid);
                if (r?.latest_status) setLiveDelivery(r.latest_status);
                if (["read", "failed"].includes(r?.latest_status)) return;
            } catch (_) { /* ignore */ }
            if (Date.now() - start < 60000) setTimeout(poll, 3000);
        };
        setLiveDelivery(null);
        poll();
        return () => { cancelled = true; };
    }, [status?.provider_message_id]);

    const send = async () => {
        setSending(true);
        setStatus(null);
        try {
            const r = await staffApi.testSendComms({
                channel,
                to: to.trim() || null,
                template_id: templateId || null,
                subject: subject.trim() || null,
                body: body.trim() || null,
            });
            setStatus(r);
            if (r.status === "sent" || r.status === "accepted") toast.success(`Accepted by Meta — ${r.to || channel}`);
            else if (r.status === "not_configured") toast.error(r.message || `${channel} isn't enabled yet`);
            else toast.error(r.message || r.error || "Send failed");
        } catch (e) {
            const err = e?.message || "Send failed";
            setStatus({ status: "failed", error: err });
            toast.error(err);
        } finally { setSending(false); }
    };

    return (
        <section className="rounded-2xl glass border hairline p-5 space-y-3" data-testid="test-comms-panel">
            <div className="flex items-center gap-2 text-gold">
                <Info className="h-4 w-4" />
                <h2 className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold/90">Test communications</h2>
            </div>
            <p className="text-sm text-white/60">
                Send yourself a test message to check email delivery or your WhatsApp Business connection. No customer is contacted.
            </p>

            <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mb-1.5 font-semibold">Channel</div>
                <select value={channel} onChange={(e) => setChannel(e.target.value)}
                        data-testid="test-channel"
                        className="w-full h-11 bg-navy border hairline rounded-md px-3 text-white text-sm">
                    <option value="email">Email</option>
                    <option value="sms">SMS</option>
                    <option value="whatsapp">WhatsApp</option>
                </select>
            </label>
            <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mb-1.5 font-semibold">
                    Send to (leave blank for your staff {channel === "email" ? "email" : "phone"})
                </div>
                <input value={to} onChange={(e) => setTo(e.target.value)}
                       placeholder={channel === "email" ? "you@example.com" : "+447…"}
                       data-testid="test-to"
                       className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
            </label>
            <label className="block">
                <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mb-1.5 font-semibold">
                    Use an existing template (optional)
                </div>
                <select value={templateId} onChange={(e) => setTemplateId(e.target.value)}
                        data-testid="test-template"
                        className="w-full h-11 bg-navy border hairline rounded-md px-3 text-white text-sm">
                    <option value="">— Custom message —</option>
                    {visibleTemplates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {channel === "whatsapp" && visibleTemplates.length === 0 && (
                    <p className="mt-1.5 text-[11px] text-white/50">
                        No WhatsApp templates are available yet. New templates appear here automatically once Meta approves them.
                    </p>
                )}
            </label>
            {channel === "email" && !templateId && (
                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mb-1.5 font-semibold">Subject</div>
                    <input value={subject} onChange={(e) => setSubject(e.target.value)}
                           placeholder="Test from Pout"
                           data-testid="test-subject"
                           className="w-full h-11 bg-transparent border hairline rounded-md px-3 text-white text-sm" />
                </label>
            )}
            {!templateId && (
                <label className="block">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-white/55 mb-1.5 font-semibold">Message</div>
                    <textarea value={body} onChange={(e) => setBody(e.target.value)}
                              rows={4}
                              data-testid="test-body"
                              className="w-full bg-transparent border hairline rounded-md p-3 text-white text-sm font-mono" />
                </label>
            )}

            <button onClick={send} disabled={sending}
                    data-testid="test-send-btn"
                    className="h-11 px-5 rounded-full bg-gold text-navy font-semibold text-sm disabled:opacity-40 inline-flex items-center gap-2">
                {sending ? "Sending…" : <>Send test {channel}</>}
            </button>

            {status && (
                <div className={`mt-2 rounded-xl border p-3 text-sm ${
                    status.status === "sent" || status.status === "accepted"
                        ? "border-amber-400/40 bg-amber-500/10 text-amber-100"
                        : status.status === "not_configured"
                            ? "border-amber-400/40 bg-amber-500/10 text-amber-100"
                            : "border-red-400/40 bg-red-500/10 text-red-100"
                }`} data-testid="test-result">
                    <div className="font-semibold uppercase tracking-[0.14em] text-[10px]">
                        {status.status === "sent" && "Sent"}
                        {status.status === "accepted" && "Accepted by Meta"}
                        {status.status === "not_configured" && "Not enabled yet"}
                        {status.status === "failed" && "Send failed"}
                    </div>
                    <div className="mt-1 text-xs space-y-1">
                        {status.status === "sent" && `Sent to ${status.to}.`}
                        {status.status === "accepted" && (
                            <>
                                <div>Sent to <span className="font-mono">{status.to}</span>.</div>
                                <div className="pt-2 flex items-center gap-2 flex-wrap">
                                    <DeliveryPill label="Accepted" active={true} />
                                    <DeliveryPill label="Sent" active={["sent","delivered","read"].includes(liveDelivery)} />
                                    <DeliveryPill label="Delivered" active={["delivered","read"].includes(liveDelivery)} />
                                    <DeliveryPill label="Read" active={liveDelivery === "read"} />
                                    {liveDelivery === "failed" && <DeliveryPill label="Failed" tone="red" active />}
                                </div>
                                <div className="pt-1 opacity-70 text-[11px]">
                                    Delivery updates automatically as the customer's phone acknowledges the message.
                                </div>
                                <details className="mt-2">
                                    <summary className="cursor-pointer text-white/50 hover:text-gold text-[10px] uppercase tracking-[0.14em]">Admin details</summary>
                                    <div className="mt-1 space-y-1 text-[11px] font-mono opacity-80">
                                        {status.meta_template_name && <div>Template: {status.meta_template_name} ({status.meta_template_language || "en_GB"}, {status.template_source})</div>}
                                        {!status.meta_template_name && <div>Send mode: free text (24-hour window {status.in_24h_window ? "open" : "closed"})</div>}
                                        {status.provider_message_id && <div>WAMID: {status.provider_message_id}</div>}
                                        {status.phone_number_id && <div>Phone number ID: {status.phone_number_id}</div>}
                                        {status.business_account_id && <div>WABA: {status.business_account_id}</div>}
                                        {status.http_status && <div>HTTP: {status.http_status}</div>}
                                    </div>
                                </details>
                            </>
                        )}
                        {status.status === "not_configured" && (status.message || "This channel isn't enabled yet.")}
                        {status.status === "failed" && (
                            <>
                                <div>{status.message || "WhatsApp message unavailable — setup required."}</div>
                                <details className="mt-2">
                                    <summary className="cursor-pointer text-white/50 hover:text-gold text-[10px] uppercase tracking-[0.14em]">Admin details</summary>
                                    <div className="mt-1 space-y-1 text-[11px] font-mono opacity-80">
                                        {status.error && <div>Error type: {status.error}</div>}
                                        {status.http_status && <div>HTTP: {status.http_status}</div>}
                                        {status.phone_number_id && <div>Phone number ID: {status.phone_number_id}</div>}
                                        {status.business_account_id && <div>WABA: {status.business_account_id}</div>}
                                    </div>
                                </details>
                            </>
                        )}
                        {(status.status === "accepted" || status.status === "failed") && status.meta_response && (
                            <details className="mt-2">
                                <summary className="cursor-pointer text-white/50 hover:text-gold text-[10px] uppercase tracking-[0.14em]">Raw Meta response (advanced)</summary>
                                <pre className="mt-1 max-h-64 overflow-auto text-[10px] leading-snug whitespace-pre-wrap break-all bg-black/30 rounded-md p-2 text-white/80">
{JSON.stringify(status.meta_response, null, 2)}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            )}
        </section>
    );
}

function Section({ icon, title, blurb, testId, children }) {
    return (
        <section className="rounded-2xl glass border hairline p-5 space-y-3" data-testid={testId}>
            <div className="flex items-center gap-2 text-gold">
                {icon}
                <h2 className="text-[10px] uppercase tracking-[0.22em] font-semibold text-gold/90">{title}</h2>
            </div>
            <p className="text-sm text-white/60">{blurb}</p>
            <div className="pt-1 space-y-2">{children}</div>
        </section>
    );
}

function ChannelRow({ icon, label, value, onChange, testKey }) {
    return (
        <div className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.03] px-3 py-2.5">
            <div className="flex items-center gap-2 text-white/85 text-sm">
                <span className="text-gold/70">{icon}</span>
                {label}
            </div>
            <button
                type="button"
                onClick={() => onChange(!value)}
                data-testid={`cd-toggle-${testKey}`}
                className={`h-8 px-4 rounded-full border text-xs font-medium transition-colors ${
                    value
                        ? "bg-emerald-500/15 border-emerald-400/50 text-emerald-200"
                        : "bg-white/[0.02] border-white/15 text-white/60"
                }`}
            >
                {value ? "On" : "Off"}
            </button>
        </div>
    );
}

function BulkRow({ label, hint, onClick, testId, danger }) {
    return (
        <div className="rounded-xl bg-white/[0.03] border border-white/10 p-3 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
                <div className={`font-medium text-sm ${danger ? "text-red-200" : "text-white"}`}>{label}</div>
                <p className="text-xs text-white/60 mt-1">{hint}</p>
            </div>
            <button onClick={onClick} data-testid={testId}
                    className={`h-10 px-4 rounded-full text-xs font-semibold border shrink-0 ${
                        danger
                            ? "border-red-400/50 text-red-300 hover:bg-red-500/10"
                            : "border-gold/50 text-gold hover:bg-gold/10"
                    }`}>
                Run
            </button>
        </div>
    );
}

function ConfirmModal({ title, message, onCancel, onConfirm, saving, danger }) {
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
             onClick={() => !saving && onCancel()}
             data-testid="cd-confirm-modal">
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-md rounded-2xl glass glass-strong border hairline p-6 space-y-4">
                <div className="flex items-center gap-2 text-gold">
                    <AlertTriangle className={`h-5 w-5 ${danger ? "text-red-300" : "text-gold"}`} />
                    <h3 className="font-serif text-white text-xl">{title}</h3>
                </div>
                <p className="text-sm text-white/75">{message}</p>
                <div className="flex gap-2 pt-2">
                    <button onClick={onCancel} disabled={saving}
                            className="flex-1 h-11 rounded-full border hairline text-white/80 text-sm">
                        Cancel
                    </button>
                    <button onClick={onConfirm} disabled={saving} data-testid="cd-confirm"
                            className={`flex-1 h-11 rounded-full font-semibold text-sm disabled:opacity-40 ${
                                danger
                                    ? "bg-red-500/90 hover:bg-red-500 text-white"
                                    : "bg-gold text-navy"
                            }`}>
                        {saving ? "Running…" : "Confirm"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CommunicationDefaults;

function BulkResultModal({ result, onClose }) {
    const p = result?.payload || {};
    const scanned = Number(p.scanned ?? p.eligible ?? 0);
    const updated = Number(p.updated ?? p.enabled ?? p.modified ?? 0);
    const skipped = Number(p.skipped ?? 0);
    const optOutsPreserved = Number(p.opt_outs_preserved ?? p.skipped_opt_out ?? 0);
    const failed = Number(p.failed ?? 0);
    const status = p.status || "complete";
    const rows = [
        { label: "Customers scanned", value: scanned, tone: "muted", testKey: "scanned" },
        { label: "Customers updated", value: updated, tone: "success", testKey: "updated" },
        { label: "Customers skipped (already up-to-date)", value: skipped, tone: "muted", testKey: "skipped" },
        { label: "Opt-outs preserved", value: optOutsPreserved, tone: "warn", testKey: "opt-outs-preserved" },
        { label: "Failed records", value: failed, tone: failed > 0 ? "danger" : "muted", testKey: "failed" },
    ];
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center px-4"
             onClick={onClose}
             data-testid="cd-result-modal">
            <div onClick={(e) => e.stopPropagation()}
                 className="w-full max-w-md rounded-2xl glass glass-strong border hairline p-6 space-y-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 text-gold">
                        <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                        <h3 className="font-serif text-white text-xl" data-testid="cd-result-title">{result.title}</h3>
                    </div>
                    <button onClick={onClose} data-testid="cd-result-close"
                            className="h-8 w-8 rounded-full border hairline text-white/70 flex items-center justify-center hover:bg-white/5">
                        <X className="h-4 w-4" />
                    </button>
                </div>
                {result.summary && (
                    <p className="text-sm text-white/70">{result.summary}</p>
                )}
                <div className="rounded-xl border hairline divide-y divide-white/5 overflow-hidden" data-testid="cd-result-counts">
                    {rows.map((r) => (
                        <div key={r.testKey}
                             className="flex items-center justify-between px-3 py-2.5 bg-white/[0.02]">
                            <span className="text-xs text-white/70">{r.label}</span>
                            <span data-testid={`cd-result-${r.testKey}`}
                                  className={`text-sm font-semibold tabular-nums ${
                                      r.tone === "success" ? "text-emerald-200"
                                      : r.tone === "warn" ? "text-amber-200"
                                      : r.tone === "danger" ? "text-red-200"
                                      : "text-white/85"
                                  }`}>
                                {r.value}
                            </span>
                        </div>
                    ))}
                    <div className="flex items-center justify-between px-3 py-2.5 bg-white/[0.03]">
                        <span className="text-xs text-white/70">Completion status</span>
                        <span data-testid="cd-result-status"
                              className="text-[10px] uppercase tracking-[0.16em] font-semibold text-emerald-200 bg-emerald-500/15 border border-emerald-400/40 rounded-full px-2 py-0.5">
                            {status}
                        </span>
                    </div>
                </div>
                <div className="flex justify-end pt-1">
                    <button onClick={onClose} data-testid="cd-result-done"
                            className="h-11 px-6 rounded-full bg-gold text-navy font-semibold text-sm">
                        Done
                    </button>
                </div>
            </div>
        </div>
    );
}


/* -----------------------------------------------------------------------
 * WhatsApp Business connection status (Feb 2026 — Phase 1)
 * Read-only card. All secrets remain in the backend .env; the UI only sees
 * a redacted hint (e.g. "•••abcd") for the access token.
 * -------------------------------------------------------------------- */
function WhatsAppStatusCard() {
    const [snap, setSnap] = React.useState(null);
    const [err, setErr] = React.useState(null);
    React.useEffect(() => {
        staffApi.whatsappStatus().then(setSnap).catch((e) => setErr(e?.message || "Unable to load"));
    }, []);
    const stateStyle = {
        connected: "border-emerald-500/50 text-emerald-200 bg-emerald-500/10",
        disabled: "border-amber-400/50 text-amber-200 bg-amber-500/10",
        missing_keys: "border-red-400/50 text-red-200 bg-red-500/10",
        missing_verify_token: "border-red-400/50 text-red-200 bg-red-500/10",
    };
    const badge = snap ? (stateStyle[snap.connection_state] || "border-white/20 text-white/70") : "border-white/20 text-white/70";
    return (
        <section className="surface-highlight rounded-2xl p-5" data-testid="wa-status-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-gold/85">WhatsApp Business</div>
                    <h2 className="mt-1 font-serif text-xl text-white">Automated messaging</h2>
                    <p className="mt-1 text-xs text-white/60 max-w-lg">
                        Sends WhatsApp confirmations, reminders and follow-ups automatically. Templates and setup are handled behind the scenes.
                    </p>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.16em] px-3 py-1 rounded-full border ${badge}`} data-testid="wa-status-badge">
                    {snap ? snap.connection_label : (err || "Loading…")}
                </span>
            </div>
            {snap && (
                <>
                    <p className="mt-4 text-xs text-white/70">
                        {snap.connection_state === "connected"
                            ? "WhatsApp automation is live."
                            : snap.connection_state === "disabled"
                                ? "WhatsApp is turned off. Ask an admin to enable it after your controlled test."
                                : "Setup incomplete. Ask an admin to finish the WhatsApp connection."}
                    </p>
                    <details className="mt-3">
                        <summary className="cursor-pointer text-[10px] uppercase tracking-[0.16em] text-white/50 hover:text-gold">Admin details</summary>
                        <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
                            <StatusRow label="Kill switch" value={snap.enabled ? "On (live sends allowed)" : "Off (no live sends)"} tone={snap.enabled ? "on" : "off"} />
                            <StatusRow label="Access token" value={snap.has_access_token ? snap.access_token_hint : "Not set"} tone={snap.has_access_token ? "on" : "off"} testId="wa-token-hint" />
                            <StatusRow label="Phone number ID" value={snap.phone_number_id || "Not set"} tone={snap.phone_number_id ? "on" : "off"} />
                            <StatusRow label="Business account ID" value={snap.business_account_id || "Not set"} tone={snap.business_account_id ? "on" : "off"} />
                            <StatusRow label="Verify token" value={snap.verify_token_set ? "Set" : "Not set"} tone={snap.verify_token_set ? "on" : "off"} />
                            <StatusRow label="App secret" value={snap.app_secret_set ? "Set" : "Not set"} tone={snap.app_secret_set ? "on" : "off"} />
                            <StatusRow label="API version" value={snap.api_version} tone="on" />
                            <StatusRow label="Webhook URL" value={snap.webhook_callback_url || "Set WHATSAPP_WEBHOOK_PUBLIC_ORIGIN"} tone={snap.webhook_callback_url ? "on" : "off"} mono />
                            <StatusRow label="Customer-service handoff" value={snap.service_handoff_number} tone="on" mono />
                        </dl>
                    </details>
                </>
            )}
            <p className="mt-4 text-[11px] text-white/45">
                Automations stay disabled until setup is complete. Send a single-recipient test first.
            </p>
        </section>
    );
}

function StatusRow({ label, value, tone, mono, testId }) {
    const dot = tone === "on" ? "bg-emerald-400" : tone === "off" ? "bg-white/25" : "bg-amber-400";
    return (
        <div className="flex items-start gap-2" data-testid={testId}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${dot}`} />
            <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.16em] text-white/50">{label}</div>
                <div className={`text-white/85 truncate ${mono ? "font-mono text-[11px]" : ""}`}>{value}</div>
            </div>
        </div>
    );
}

function DeliveryPill({ label, active, tone = "gold" }) {
    const base = "px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] border";
    const on =
        tone === "red"
            ? "bg-red-500/20 border-red-400/50 text-red-100"
            : "bg-gold/20 border-gold/60 text-gold";
    const off = "bg-white/[0.03] border-white/15 text-white/40";
    return <span className={`${base} ${active ? on : off}`}>{label}</span>;
}
